import React, { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { ShoppingCart, Phone, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const ReservationsTab = () => {
  const { language } = useLanguage();
  const { token } = useAuth();
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchReservations = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/pharmacy/reservations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReservations(response.data);
    } catch (error) {
      console.error('Error fetching reservations:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchReservations();
  }, [fetchReservations]);

  const updateReservationStatus = async (reservationId, newStatus) => {
    try {
      await axios.patch(`${API_URL}/pharmacy/reservations/${reservationId}?status=${newStatus}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(language === 'fr' ? 'Statut mis à jour' : 'Status updated');
      fetchReservations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-orange-200 text-orange-800',
      confirmed: 'bg-blue-200 text-blue-800',
      ready: 'bg-purple-200 text-purple-800',
      completed: 'bg-green-200 text-green-800',
      cancelled: 'bg-gray-200 text-gray-800'
    };
    const labels = {
      pending: language === 'fr' ? 'En attente' : 'Pending',
      confirmed: language === 'fr' ? 'Confirmé' : 'Confirmed',
      ready: language === 'fr' ? 'Prêt' : 'Ready',
      completed: language === 'fr' ? 'Terminé' : 'Completed',
      cancelled: language === 'fr' ? 'Annulé' : 'Cancelled'
    };
    return { style: styles[status] || styles.pending, label: labels[status] || status };
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ShoppingCart className="w-5 h-5 text-[#C9A086]" />
            {language === 'fr' ? 'Réservations Click & Collect' : 'Click & Collect Reservations'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-8 h-8 animate-spin text-[#C9A086]" />
            </div>
          ) : reservations.length === 0 ? (
            <div className="text-center py-8 text-[#78716C]">
              <ShoppingCart className="w-12 h-12 mx-auto mb-4 opacity-30" />
              <p>{language === 'fr' ? 'Aucune réservation' : 'No reservations'}</p>
            </div>
          ) : (
            <div className="space-y-4">
              {reservations.map((reservation) => {
                const status = getStatusBadge(reservation.status);
                return (
                  <Card 
                    key={reservation.id} 
                    className={`border-l-4 ${
                      reservation.status === 'pending' ? 'border-l-orange-500 bg-orange-50' :
                      reservation.status === 'confirmed' ? 'border-l-blue-500 bg-blue-50' :
                      reservation.status === 'ready' ? 'border-l-purple-500 bg-purple-50' :
                      reservation.status === 'completed' ? 'border-l-green-500 bg-green-50' :
                      'border-l-gray-500 bg-gray-50'
                    }`}
                  >
                    <CardContent className="p-4">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <span className={`px-2 py-1 text-xs font-semibold rounded ${status.style}`}>
                              {status.label}
                            </span>
                            <span className="text-sm text-gray-500">
                              #{reservation.id?.slice(-6)}
                            </span>
                          </div>
                          <h3 className="font-semibold text-[#5D4037]">{reservation.customer_name}</h3>
                          <p className="text-sm text-[#78716C]">
                            <Phone className="w-3 h-3 inline mr-1" />
                            <a href={`tel:${reservation.customer_phone}`} className="hover:text-[#C9A086]">
                              {reservation.customer_phone}
                            </a>
                          </p>
                          <div className="mt-3">
                            <p className="text-sm font-medium text-[#5D4037]">
                              {language === 'fr' ? 'Produits :' : 'Products:'}
                            </p>
                            <ul className="text-sm text-[#78716C] mt-1">
                              {reservation.items?.map((item, idx) => (
                                <li key={idx}>• {item.product_name || item.name} x{item.quantity}</li>
                              ))}
                            </ul>
                          </div>
                          <p className="text-lg font-bold text-[#C9A086] mt-2">
                            {reservation.total_amount?.toLocaleString('fr-FR')} FCFA
                          </p>
                          <p className="text-xs text-gray-500 mt-1">
                            {new Date(reservation.created_at).toLocaleString('fr-FR')}
                          </p>
                        </div>
                        <div className="flex flex-col gap-2 ml-4">
                          {reservation.status === 'pending' && (
                            <>
                              <Button
                                size="sm"
                                className="bg-blue-600 hover:bg-blue-700"
                                onClick={() => updateReservationStatus(reservation.id, 'confirmed')}
                              >
                                {language === 'fr' ? 'Confirmer' : 'Confirm'}
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-red-600 border-red-300 hover:bg-red-50"
                                onClick={() => updateReservationStatus(reservation.id, 'cancelled')}
                              >
                                {language === 'fr' ? 'Annuler' : 'Cancel'}
                              </Button>
                            </>
                          )}
                          {reservation.status === 'confirmed' && (
                            <Button
                              size="sm"
                              className="bg-purple-600 hover:bg-purple-700"
                              onClick={() => updateReservationStatus(reservation.id, 'ready')}
                            >
                              {language === 'fr' ? 'Prêt' : 'Ready'}
                            </Button>
                          )}
                          {reservation.status === 'ready' && (
                            <Button
                              size="sm"
                              className="bg-green-600 hover:bg-green-700"
                              onClick={() => updateReservationStatus(reservation.id, 'completed')}
                            >
                              {language === 'fr' ? 'Récupéré' : 'Collected'}
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ReservationsTab;
